import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { breakpoints, mq } from '@styles/breakpoints';
import { Image } from '@model/common';
import Link from 'next/link';
import { getImageUrl } from '@util/images';
import { mixins } from '@styles/mixins';

/* ***************** *
 *       Types       *
 * ***************** */
export interface InfoRowProps {
  theme?: Theme;
  testId?: string;
  image: Image;
  title: string;
  description: string;
  ctaTitle: string | null;
  ctaUrl?: string;
  openInNewTab: boolean;
  ctaRender?: () => JSX.Element;
}

/* ***************** *
 *       Styles      *
 * ***************** */

export const InfoRowContainer: any = styled.div(({ theme }: ThemeProps) => ({
  ...mixins(theme).card,
  display: 'flex',
  marginBottom: theme.custom.spacing.xLarge,
  padding: theme.custom.spacing.large,
  flexDirection: 'column',
  [mq.medium]: {
    flexDirection: 'row'
  },
  ':last-child': {
    marginBottom: 0
  }
}));

interface ImageWrapperProps extends ThemeProps {
  imageUrl: string;
}
const ImageWrapper: any = styled.div(({ theme, imageUrl }: ImageWrapperProps) => ({
  backgroundImage: `url(${imageUrl})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minWidth: 100,
  height: 200,
  marginBottom: theme.custom.spacing.large,
  ['img']: {
    display: 'none'
  },

  [mq.medium]: {
    marginBottom: 0,
    height: 'auto',
    maxWidth: 100,
    backgroundImage: 'none',

    ['img']: {
      display: 'block',
      width: '100%'
    }
  }
}));

const ContentContainer: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'space-between',
  flexDirection: 'column',
  [mq.medium]: {
    columnGap: theme.custom.spacing.large,
    flexDirection: 'row'
  }
}));

const TextContainer: any = styled.div(() => ({
  margin: '0 auto'
}));

const Heading: any = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  fontWeight: 'bold',
  margin: 0,
  marginBottom: theme.custom.spacing.xSmall,
  color: theme.custom.colors.group4.base
}));

const Paragraph: any = styled.p(({ theme }: ThemeProps) => ({
  margin: 0,
  marginBottom: theme.custom.spacing.medium,
  color: theme.custom.colors.group4.base,
  ...(theme.custom.typography.paragraph as any),
  [mq.medium]: {
    marginBottom: 0
  }
}));

const ActionButton: any = styled(Button)({
  width: '100%'
});

const ButtonContainer: any = styled.div({
  display: 'flex',
  alignItems: 'center',
  minWidth: 150
});

const RowImage: any = styled.img({
  borderRadius: 4,
  height: '100%',
  objectFit: 'contain'
});

/* ********************** *
 *    InfoRowComponent    *
 * ********************** */

export const InfoRowComponent = ({
  title,
  image,
  description,
  ctaTitle,
  ctaUrl,
  testId,
  openInNewTab,
  ctaRender
}: InfoRowProps) => {
  const imageUrl: string = getImageUrl(image.url, breakpoints.small);

  return (
    <InfoRowContainer data-testid={testId || TestId.infoRow.main}>
      <ContentContainer>
        <ImageWrapper imageUrl={imageUrl}>
          <RowImage src={imageUrl} alt={title} />
        </ImageWrapper>
        <TextContainer>
          <Heading>{title}</Heading>
          <Paragraph>{description}</Paragraph>
        </TextContainer>
        {!ctaRender && ctaUrl && (
          <ButtonContainer>
            {openInNewTab ? (
              <ActionButton
                label={ctaTitle}
                variant={ButtonVariant.OUTLINED}
                color={ButtonColor.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={() => window.open(ctaUrl)}
              />
            ) : (
              <Link prefetch={false} href={ctaUrl}>
                <ActionButton
                  label={ctaTitle}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.OUTLINED}
                  color={ButtonColor.SECONDARY}
                />
              </Link>
            )}
          </ButtonContainer>
        )}
        {ctaRender && ctaRender()}
      </ContentContainer>
    </InfoRowContainer>
  );
};

export const InfoRow = withTheme(InfoRowComponent);
